"use client"

import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"

export interface TocHeading {
  id: string
  text: string
  level: 2 | 3
}

interface TableOfContentsProps {
  headings: TocHeading[]
}

function scrollToHeading(id: string) {
  const element = document.getElementById(id)
  if (!element) return false

  const top = element.getBoundingClientRect().top + window.scrollY - 100
  window.scrollTo({ top, behavior: "smooth" })
  return true
}

interface HeadingAnchorLinkProps {
  id: string
  children: ReactNode
}

export function HeadingAnchorLink({ id, children }: HeadingAnchorLinkProps) {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        !scrollToHeading(id)
      ) {
        return
      }

      event.preventDefault()
      window.history.pushState(null, "", `#${id}`)
    },
    [id]
  )

  return (
    <a
      href={`#${id}`}
      onClick={handleClick}
      className="inline text-inherit no-underline transition-colors hover:text-[#1d7df2] focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1d7df2] focus-visible:ring-offset-2"
    >
      {children}
    </a>
  )
}

export function TableOfContents({ headings }: TableOfContentsProps) {
  const t = useTranslations("blog.article")
  const [activeId, setActiveId] = useState<string>("")
  const [expanded, setExpanded] = useState(false)
  const sectionHeadings = useMemo(
    () => headings.filter((heading) => heading.level === 2),
    [headings]
  )
  const visibleHeadings = expanded ? sectionHeadings : sectionHeadings.slice(0, 10)
  const hasHiddenHeadings = sectionHeadings.length > 10

  useEffect(() => {
    if (sectionHeadings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        }
      },
      { rootMargin: "-80px 0px -80% 0px", threshold: 0 }
    )

    for (const heading of sectionHeadings) {
      const el = document.getElementById(heading.id)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [sectionHeadings])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault()
      if (scrollToHeading(id)) {
        setActiveId(id)
      }
    },
    []
  )

  if (sectionHeadings.length === 0) return null

  return (
    <nav aria-label={t("tocLabel")} className="w-full max-w-[236px]">
      <p className="blog-toc-title">
        {t("tocTitle")}
      </p>
      <ul className={cn("blog-toc-list", hasHiddenHeadings && !expanded && "blog-toc-list-collapsed")}>
        {visibleHeadings.map((heading, index) => (
          <li key={`${heading.id}-${index}`}>
            <a
              href={`#${heading.id}`}
              onClick={(e) => handleClick(e, heading.id)}
              title={heading.text}
              className={cn(
                "blog-toc-link",
                "blog-toc-l2",
                activeId === heading.id && "blog-toc-active"
              )}
            >
              <span className="line-clamp-2">{heading.text}</span>
            </a>
          </li>
        ))}
      </ul>
      {hasHiddenHeadings && (
        <button
          type="button"
          className="blog-toc-toggle mt-3 inline-flex cursor-pointer items-center border-0 bg-transparent p-0 text-[12px] font-bold leading-[1.4] text-[#1d7df2] hover:text-[#155ec7] hover:underline hover:underline-offset-[3px]"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded
            ? t("tocShowFewer")
            : t("tocShowAll", { count: sectionHeadings.length })}
        </button>
      )}
    </nav>
  )
}
